/**
 * One place that works out the person's onboarding for the sidebar's checklist, the header's help menu and the
 * tour, so the three agree and nothing is asked twice (the shell renders this once; the mobile drawer's second
 * sidebar reads from it). It is optional UI: without it, or while anything is loading or failing, the checklist
 * and tour simply are not drawn.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import { portal } from "@/lib/api";
import { config } from "@/lib/config";
import {
  buildChecklist,
  onboardingKey,
  useOnboardingState,
  useOtherPeople,
  useRecordTour,
  useSetChecklistDismissed,
  type ChecklistStep,
} from "@/lib/onboarding";
import { useOverview } from "@/lib/queries";
import type { OnboardingState, TourOutcome } from "@/lib/types";

interface OnboardingContextValue {
  /** The steps this person is shown; empty when their role has nothing to do here or it is still loading. */
  steps: readonly ChecklistStep[];
  /** Whether the checklist exists for this person at all (it can then be hidden and brought back). */
  applicable: boolean;
  dismissed: boolean;
  dismiss: () => void;
  restore: () => void;
  /** The moment right after the last step was ticked, shown once. */
  celebrating: boolean;
  endCelebration: () => void;
  tourOpen: boolean;
  startTour: () => void;
  endTour: (outcome: TourOutcome) => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

/** Null outside the shell (and in tests of a single page): nothing onboarding-related is drawn then. */
export function useOnboarding(): OnboardingContextValue | null {
  return useContext(OnboardingContext);
}

/** A modal on screen (the mobile menu; the shared Modal, a native <dialog>, as in "Add a new business"): the tour does not start on top of it. */
const modalIsOpen = () => document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]') !== null;

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const user = useUser();
  const { orgId, canAny, permissionsLoading } = useOrganization();
  const qc = useQueryClient();
  const overview = useOverview();
  const saved = useOnboardingState();
  const team = useOtherPeople(!permissionsLoading && canAny("members:invite"));
  const { mutate: setChecklistDismissed } = useSetChecklistDismissed();
  const { mutate: recordTour } = useRecordTour();
  const [celebratingIn, setCelebratingIn] = useState<string | null>(null);
  const [tourOpen, setTourOpen] = useState(false);

  const usage = overview.data?.usage;
  const ready = !permissionsLoading && usage !== undefined && team.settled;
  const steps = useMemo(
    () =>
      ready
        ? buildChecklist({
            emailVerified: user.email_verified,
            usage,
            canAny,
            otherPeople: team.count,
            builderUrl: config.builderUrl,
          })
        : [],
    [ready, usage, user.email_verified, canAny, team.count],
  );
  const applicable = steps.length > 0;
  const allDone = applicable && steps.every((s) => s.done);

  // ── finishing the checklist ──
  const sawUnfinished = useRef(new Set<string>());
  const reported = useRef(new Set<string>());
  const complete = useMutation({
    mutationFn: () => portal.completeChecklist(orgId),
    onSuccess: ({ first_time, ...state }) => {
      qc.setQueryData<OnboardingState>(onboardingKey(orgId), state);
      // Celebrate only a finish seen happening here: someone who finished long ago is told nothing.
      if (first_time && sawUnfinished.current.has(orgId)) setCelebratingIn(orgId);
    },
  });
  const { mutate: reportCompletion } = complete;
  const completedAt = saved.data?.checklist.completed_at;
  const stateLoaded = saved.data !== undefined;
  useEffect(() => {
    if (applicable && !allDone) sawUnfinished.current.add(orgId);
  }, [applicable, allDone, orgId]);
  useEffect(() => {
    if (!allDone || !stateLoaded || completedAt || reported.current.has(orgId)) return;
    reported.current.add(orgId);
    reportCompletion(undefined, { onError: () => reported.current.delete(orgId) });
  }, [allDone, stateLoaded, completedAt, orgId, reportCompletion]);

  // ── the tour ──
  const endTour = useCallback(
    (outcome: TourOutcome) => {
      setTourOpen(false);
      recordTour(outcome);
    },
    [recordTour],
  );
  const autoStarted = useRef(false);
  const tourSeen = saved.data === undefined ? undefined : saved.data.tour !== null;
  const settled = ready || overview.isError;
  useEffect(() => {
    if (autoStarted.current || tourSeen !== false || !settled || modalIsOpen()) return;
    autoStarted.current = true;
    setTourOpen(true);
  }, [tourSeen, settled]);

  const value = useMemo<OnboardingContextValue>(
    () => ({
      steps,
      applicable,
      dismissed: saved.data?.checklist.dismissed ?? false,
      dismiss: () => setChecklistDismissed(true),
      restore: () => {
        setCelebratingIn(null);
        setChecklistDismissed(false);
      },
      celebrating: celebratingIn === orgId && !(saved.data?.checklist.dismissed ?? false),
      endCelebration: () => setCelebratingIn(null),
      tourOpen,
      startTour: () => setTourOpen(true),
      endTour,
    }),
    [steps, applicable, saved.data, setChecklistDismissed, celebratingIn, orgId, tourOpen, endTour],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}
